// Lightweight dependency-free charts (SVG/CSS) — avoids Recharts ref bug under React 19.

export const VBarChart = ({ data, height = 220, unit = "", accent = "#4F46E5" }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="w-full" style={{ height }} data-testid="vbar-chart">
      <div className="flex items-end gap-2" style={{ height: height - 28 }}>
        {data.map((d, i) => (
          <div key={`${d.label}-${i}`} className="flex-1 flex flex-col items-center justify-end h-full group" data-testid={`vbar-${i}`}>
            <span className="text-[10px] font-bold mb-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {d.value}
              {unit}
            </span>
            <div
              className="w-full rounded-t-lg border-2 border-slate-900 transition-all group-hover:brightness-95"
              style={{ height: `${Math.max((d.value / max) * 100, 2)}%`, background: d.fill || accent }}
              title={`${d.label}: ${d.value}${unit}`}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-1.5">
        {data.map((d, i) => (
          <span key={`${d.label}-${i}`} className="flex-1 text-center text-[10px] font-bold text-slate-600 truncate">
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export const HBarChart = ({ data, unit = " L" }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2.5" data-testid="hbar-chart">
      {data.map((d, i) => (
        <div key={`${d.label}-${i}`} className="flex items-center gap-3" data-testid={`hbar-${d.label}`}>
          <span className="w-24 shrink-0 text-xs font-bold truncate" title={d.label}>
            {d.label}
          </span>
          <div className="flex-1 h-6 rounded-lg border-2 border-slate-900 bg-white overflow-hidden">
            <div
              className="h-full rounded-r-md transition-all"
              style={{ width: `${Math.max((d.value / max) * 100, 4)}%`, background: d.fill }}
              title={`${d.value}${unit}`}
            />
          </div>
          <span className={`w-28 shrink-0 text-xs font-bold ${d.over ? "text-[#EF4444]" : "text-slate-700"}`}>
            {d.value}
            {unit}
            {d.ref != null ? ` · réf ${d.ref}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
};

export const LineChartMini = ({ data, height = 200, refs = [], unit = "" }) => {
  const values = data.map((d) => d.value);
  const all = [...values, ...refs.map((r) => r.y)];
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.1;
  min -= pad;
  max += pad;
  const range = max - min || 1;
  const yPct = (v) => 100 - ((v - min) / range) * 100;
  const xPct = (i) => (data.length === 1 ? 50 : (i / (data.length - 1)) * 100);
  const pts = data.map((d, i) => ({ x: xPct(i), y: yPct(d.value), v: d.value, label: d.label }));
  const path = pts.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  return (
    <div className="w-full relative" style={{ height }} data-testid="line-chart">
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="overflow-visible">
        {refs.map((r, i) => (
          <line
            key={`ref-${i}`}
            x1="0"
            x2="100"
            y1={yPct(r.y)}
            y2={yPct(r.y)}
            stroke={r.color}
            strokeWidth="1.5"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path d={path} fill="none" stroke="#4F46E5" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      {pts.map((p, i) => (
        <div
          key={`dot-${i}`}
          className="absolute w-2.5 h-2.5 rounded-full border-2 border-slate-900 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${p.x}%`, top: `${p.y}%`, background: "#FBBF24" }}
          title={`${p.label}: ${p.v}${unit}`}
        />
      ))}
      {refs.map((r, i) => (
        <span
          key={`lbl-${i}`}
          className="absolute right-0 text-[10px] font-bold px-1.5 rounded bg-white/90 border border-slate-300"
          style={{ top: `calc(${yPct(r.y)}% - 8px)`, color: r.color }}
        >
          {r.label}
        </span>
      ))}
    </div>
  );
};
